<template>
  <div
    v-show="visible"
    :class="classForType"
    class="pp-message"
  >
    <div
      class="message-icon"
      :class="props.type"
    />
    <div class="message-text">
      <span>
        {{ props.message }}
      </span>
      <span
        v-if="showRetryButton && props.option?.layout === 'inline'"
        class="retry-button"
        @click="handleCallback"
      >
        {{ props.option?.title || $t('retry') }}
      </span>
    </div>

    <div
      v-if="showRetryButton && props.option?.layout !== 'inline'"
      class="retry-button"
      @click="handleCallback"
    >
      {{ props.option?.title || $t('retry') }}
    </div>

    <div
      v-if="props.showClose"
      class="close-icon"
      @click="hide"
    />
  </div>
</template>

<script lang="ts" setup>
import { useMediaQuery } from '@vueuse/core'
import { computed, onMounted, ref } from 'vue'

interface MessageOptions {
  message: string
  type: 'null' | 'error' | 'success' | 'warning' | 'loading'
  duration: number
  showClose?: boolean
  option?: {
    title?: string
    callback?: VoidFunction
    layout?: 'between' | 'inline'
  }
  offset?: number
}

const props = defineProps<MessageOptions>()

const showRetryButton = computed(() => props.option?.title || props.option?.callback)
const handleCallback = async () => {
  try {
    await props.option?.callback?.()
  } catch (err) {
    console.error('[handleCallback: ]', err)
  }
}

const classForType = computed(() => `pp-message-${props.type}`)

let timer: ReturnType<typeof setTimeout>
const visible = ref(false)
const hide = () => {
  visible.value = false
  clearTimeout(timer)
}
const show = () => {
  visible.value = true
  timer = setTimeout(() => {
    hide()
  }, props.duration)
}

const isMobile = useMediaQuery('only screen and (max-width: 768px)')
onMounted(() => {
  show()
})

const topPositionOffset = computed(() => {
  if (isMobile.value) {
    return 'unset'
  }
  return props.offset || '168px'
})
const bottomPositionOffset = computed(() => {
  if (!isMobile.value) {
    return 'unset'
  }
  return props.offset || '82px'
})

defineExpose({
  hide,
})
</script>

<style lang="less" scoped>
@import '@/assets/styles/util.less';

.pp-message {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  z-index: 9999 !important;
  top: v-bind(topPositionOffset);
  bottom: v-bind(bottomPositionOffset);
  left: 50%;
  min-width: 366px;
  max-width: 662px;
  transform: translateX(-50%);
  min-height: 48px;
  line-height: 1.2;
  box-sizing: border-box;
  padding: 12px 24px;
  background: #3c3c3c;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
}

@media screen and (max-width: @mobile-break-width) {
  .pp-message {
    width: calc(100vw - 12px);
  }
}

.message-icon {
  display: flex;
  align-items: center;
  margin-right: 10px;
  width: 20px;
  height: 20px;

  @type: error, loading, success, warning;
  each(@type, {
    &.@{value} {
      @url: './icons/@{value}.svg';
      background: url("@{url}") var(--no-repeat-center)/contain;
    }
  })
    @keyframes
    Loading {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  &.loading {
    animation: Loading 1s ease-in-out infinite both;
  }
}

.message-text {
  flex: 1;
  text-align: left;
  margin-right: auto;
}

.retry-button {
  font-weight: 600;
  color: #6794ff;
  margin: 0 12px;
  cursor: pointer;
  user-select: none;

  &span {
    margin-left: 5px;
  }
}

.close-icon {
  width: 24px;
  height: 24px;
  background: url('@/assets/images/drive-layout/close.svg') no-repeat center/cover;
  cursor: pointer;
}
</style>
./icons/WarningIcon.vue
